/**
 * Company settings (Admin → Settings), read by every screen that needs a
 * default. One query, cached for a minute, shared across the app; an
 * update in Admin invalidates it so the board picks the new values up
 * without a reload.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type FieldJobsSettings, type SchedulingSettings } from './api.js';

/** Must match SCHEDULING_DEFAULTS in the API — what the app ran with before settings existed. */
export const SCHEDULING_FALLBACK: SchedulingSettings = {
  defaultJobHours: 2,
  dayStartHour: 8,
  dayEndHour: 16,
};

export const SCHEDULING_SETTINGS_KEY = ['settings', 'scheduling'] as const;

/**
 * Always returns a usable object — the fallback until the fetch lands, or
 * if it fails (a settings outage must not blank the board).
 */
export function useSchedulingSettings(): SchedulingSettings {
  const query = useQuery({
    queryKey: SCHEDULING_SETTINGS_KEY,
    queryFn: ({ signal }) => api.settings.scheduling(signal),
    staleTime: 60_000,
  });
  return query.data ?? SCHEDULING_FALLBACK;
}

export function useInvalidateSchedulingSettings(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: SCHEDULING_SETTINGS_KEY });
}

export const FIELD_JOBS_FALLBACK: FieldJobsSettings = {
  enabled: false,
  billToCustomerNo: null,
  billToCustomerName: null,
  typeUdfKey: 'SALES TYP',
  requirePo: true,
};

export const FIELD_JOBS_SETTINGS_KEY = ['settings', 'field-jobs'] as const;

/** Field-created jobs settings; the fallback (disabled) until loaded. */
export function useFieldJobsSettings(): FieldJobsSettings {
  const query = useQuery({
    queryKey: FIELD_JOBS_SETTINGS_KEY,
    queryFn: ({ signal }) => api.settings.fieldJobs(signal),
    staleTime: 60_000,
  });
  return query.data ?? FIELD_JOBS_FALLBACK;
}

export function useInvalidateFieldJobsSettings(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: FIELD_JOBS_SETTINGS_KEY });
}

/** "7 AM", "12 PM", "5 PM" — for the day-window pickers and labels. */
export function hourLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}
